/* eslint-disable max-lines-per-function */

import { noop } from 'lodash';
import { useMemo } from 'react';

import {
   HealthcareProviderNotificationPackage,
   HealthcareProviderNotificationPackageQuery,
   HealthcareProviderNotificationPackageStatus,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import {
   assertUnreachable,
   createInterpolatedTranslationKey,
   dateFormatter,
   integerFormatter,
   InterpolatedTranslationKey,
   Translate,
} from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   IconSize,
   IconWrapper,
   JustifyContent,
   QuestionmarkCircleIcon,
   StyledMargin,
   Table,
   TableColumnConfiguration,
   Tooltip,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

const PAGE_SIZE = 10;

const useTooltipContent = (
   props: HealthcareProviderNotificationPackage
): string | InterpolatedTranslationKey | null => {
   return useMemo(() => {
      if (props.status === 'INACTIVE') {
         return null;
      }

      if (!props.validFrom && !props.autoRefreshDate) {
         return patientPhoneNotificationTranslationKeys.packagesTable.noAutoRefreshDayOfMonth;
      }

      if (props.autoRefreshDate) {
         return createInterpolatedTranslationKey(
            patientPhoneNotificationTranslationKeys.packagesTable.autoRefreshDate,
            {
               date: dateFormatter(props.autoRefreshDate),
            }
         );
      }
      return null;
   }, [props.autoRefreshDate, props.status, props.validFrom]);
};

const PackageTooltip = (props: HealthcareProviderNotificationPackage): JSX.Element | null => {
   const tooltipContent = useTooltipContent(props);

   if (tooltipContent === null) {
      return null;
   }

   return (
      <StyledMargin left={-6}>
         <Tooltip testId={'packageTooltip'} content={tooltipContent}>
            <IconWrapper testId={'packageIconWrapper'} size={IconSize.SMALL}>
               <QuestionmarkCircleIcon />
            </IconWrapper>
         </Tooltip>
      </StyledMargin>
   );
};

const getStatus = (status: HealthcareProviderNotificationPackageStatus): string => {
   switch (status) {
      case 'ACTIVE':
         return patientPhoneNotificationTranslationKeys.packagesTable.active;
      case 'INACTIVE':
         return patientPhoneNotificationTranslationKeys.packagesTable.inactive;
      case 'UTILIZED':
         return patientPhoneNotificationTranslationKeys.packagesTable.utilized;
      default:
         return assertUnreachable(status);
   }
};

const usePackageColumns = (): TableColumnConfiguration<HealthcareProviderNotificationPackage>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         name: '100%',
         size: '168px',
         remaining: '180px',
         validFrom: '120px',
         validTo: '118px',
         icon: '24px',
         status: '112px',
      });

      return [
         {
            label: patientPhoneNotificationTranslationKeys.packagesTable.name,
            width: columnsWidth.name,
            accessor: 'name',
            name: 'name',
            sortingDisabled: true,
         },
         {
            label: patientPhoneNotificationTranslationKeys.packagesTable.size,
            width: columnsWidth.size,
            accessor: 'size',
            name: 'size',
            formatter: integerFormatter,
            justifyContent: JustifyContent.END,
            sortingDisabled: true,
         },
         {
            label: patientPhoneNotificationTranslationKeys.packagesTable.remaining,
            width: columnsWidth.remaining,
            accessor: 'remaining',
            name: 'remaining',
            formatter: integerFormatter,
            justifyContent: JustifyContent.END,
            sortingDisabled: true,
         },
         {
            label: patientPhoneNotificationTranslationKeys.packagesTable.validFrom,
            width: columnsWidth.validFrom,
            accessor: 'validFrom',
            name: 'validFrom',
            renderer: ({ value }: ValueRendererProps<HealthcareProviderNotificationPackage>) => (
               <>{value ? dateFormatter(value) : '-'}</>
            ),
            justifyContent: JustifyContent.END,
            sortingDisabled: true,
         },
         {
            label: patientPhoneNotificationTranslationKeys.packagesTable.validTo,
            width: columnsWidth.validTo,
            accessor: 'validTo',
            name: 'validTo',
            renderer: ({ value }: ValueRendererProps<HealthcareProviderNotificationPackage>) => (
               <>
                  {value ? (
                     dateFormatter(value)
                  ) : (
                     <Translate>{patientPhoneNotificationTranslationKeys.packagesTable.indefinitely}</Translate>
                  )}
               </>
            ),
            justifyContent: JustifyContent.END,
            sortingDisabled: true,
         },
         {
            width: columnsWidth.icon,
            accessor: noop,
            name: 'icon',
            renderer: ({ bean }: ValueRendererProps<HealthcareProviderNotificationPackage>) => (
               <PackageTooltip {...bean} />
            ),
            sortingDisabled: true,
         },
         {
            label: patientPhoneNotificationTranslationKeys.packagesTable.status,
            width: columnsWidth.status,
            accessor: 'status',
            name: 'status',
            renderer: ({ value }: ValueRendererProps<HealthcareProviderNotificationPackage>) => (
               <Translate>{getStatus(value)}</Translate>
            ),
            sortingDisabled: true,
         },
      ];
   }, []);
};

const usePackageDataConfiguration = (
   showInactivePackages: boolean | undefined
): LazyLoadedDataConfigurationByCqrsQuery<HealthcareProviderNotificationPackage> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<HealthcareProviderNotificationPackage>(() => {
      const query = new HealthcareProviderNotificationPackageQuery();
      if (!showInactivePackages) {
         query.status = Slot.of<HealthcareProviderNotificationPackageStatus[]>(['ACTIVE', 'UTILIZED']);
      }
      return query;
   });
};

type PatientPhoneNotificationPackageTableProps = {
   readonly showInactivePackagesPreference: boolean | undefined;
};

export const PatientPhoneNotificationPackageTable = (props: PatientPhoneNotificationPackageTableProps): JSX.Element => {
   const columns = usePackageColumns();
   const dataConfiguration = usePackageDataConfiguration(props.showInactivePackagesPreference);

   return (
      <Table<HealthcareProviderNotificationPackage>
         testId={'packagesTable'}
         keyExtractor={'id'}
         columns={columns}
         dataConfiguration={dataConfiguration}
         pageSize={PAGE_SIZE}
         compact
      />
   );
};
